import { ChevronLeft, RotateCcw } from "lucide-react";
import { FanMark, Wordmark } from "./FanMark";

const RED = "#E82529";

// Pill step indicator (32px active / 16px inactive, red filled <= current)
function StepPills({ step, total }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            height: 6,
            borderRadius: 3,
            transition: "all 0.2s",
            width: i + 1 === step ? 32 : 16,
            background: i + 1 <= step ? RED : "var(--zen-border-h)",
          }}
        />
      ))}
    </div>
  );
}

/**
 * Shared editorial-split shell for the form-flow pages — matches the v7 refs.
 * Desktop: top header bar + 42/58 split (left editorial photo, right form).
 * Mobile: photo banner (step + headline) on top, form below.
 */
export default function FormShell({
  step,
  total = 7,
  photo,
  photoPos = "center center",
  photoCredit,
  eyebrow,
  bigWord,
  leftSubtitle,
  title,
  uppercaseTitle = true,
  subtitle,
  onBack,
  onStartOver,
  continueLabel = "Continue",
  onContinue,
  continueDisabled = false,
  showContinue = true,
  children,
}) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--zen-bg)" }}>
      {/* ── Desktop header bar ── */}
      <div
        className="hidden md:flex items-center justify-between px-12 flex-shrink-0"
        style={{ height: 44, borderBottom: "1px solid var(--zen-border)" }}
      >
        <div className="flex items-center gap-2.5">
          <FanMark size={16} title="" />
          <Wordmark size={12} />
        </div>
        <div className="flex items-center gap-4">
          <StepPills step={step} total={total} />
          {onStartOver && (
            <button
              onClick={onStartOver}
              className="font-mono-label flex items-center gap-1 hover:opacity-70 transition-opacity"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 9, letterSpacing: "0.16em", color: "var(--zen-dim)" }}
            >
              <RotateCcw className="w-3 h-3" /> Start over
            </button>
          )}
        </div>
      </div>

      {/* ── Main split ── */}
      <div className="flex-1 flex flex-col md:grid md:grid-cols-[42fr_58fr] min-h-0">

        {/* Left — editorial photo panel */}
        <div
          className="relative overflow-hidden h-[32vh] md:h-auto md:border-r flex-shrink-0"
          style={{ borderRightColor: "var(--zen-border)" }}
        >
          {photo && photo.startsWith("/photos/") ? (
            /* Responsive photo (800 / 1200 / 1600 px, WebP + JPEG) picked and
               cropped by Fede; full colour — the fog below does the work. */
            <picture>
              <source
                type="image/webp"
                sizes="(min-width: 768px) 42vw, 100vw"
                srcSet={`${photo}-800.webp 800w, ${photo}-1200.webp 1200w, ${photo}-1600.webp 1600w`}
              />
              <img
                src={`${photo}-1200.jpg`}
                sizes="(min-width: 768px) 42vw, 100vw"
                srcSet={`${photo}-800.jpg 800w, ${photo}-1200.jpg 1200w, ${photo}-1600.jpg 1600w`}
                alt=""
                fetchPriority="high"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: photoPos, filter: "contrast(1.02)" }}
              />
            </picture>
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${photo})`,
                backgroundSize: "cover",
                backgroundPosition: photoPos,
                filter: "brightness(1.08) saturate(0.76) contrast(1.02)",
              }}
            />
          )}
          {/* Editorial veil: bottom-only fog so the big word reads, the upper
              half of the photo untouched (light fades to cream, dark to obsidian). */}
          <div className="fs-veil" />
          {photoCredit && (
            <div className="font-mono-label" style={{ position: "absolute", right: 12, bottom: 8, fontSize: 8, letterSpacing: "0.14em", color: "var(--zen-dim)", opacity: 0.85 }}>
              {photoCredit}
            </div>
          )}
          <div className="absolute inset-0 flex flex-col justify-between" style={{ padding: "24px 30px" }}>
            <div className="font-mono-label" style={{ fontSize: 9, letterSpacing: "0.22em", color: "var(--zen-text)", opacity: 0.42 }}>
              STEP {step} OF {total}
            </div>
            <div>
              {eyebrow && (
                <div className="font-mono-label" style={{ fontSize: 10, letterSpacing: "0.28em", color: RED, marginBottom: 14, opacity: 0.9 }}>
                  ☞ {eyebrow}
                </div>
              )}
              <div
                style={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 900,
                  fontSize: "clamp(2.6rem, 5.4vw, 3.25rem)",
                  lineHeight: 0.88,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--zen-text)",
                  whiteSpace: "pre-line",
                }}
              >
                {bigWord}
              </div>
              {leftSubtitle && (
                <div className="font-curator" style={{ marginTop: 16, fontWeight: 600, fontSize: "clamp(14px, 1.4vw, 17px)", lineHeight: 1.55, color: "var(--zen-sub)", maxWidth: 280 }}>
                  {leftSubtitle}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div
          className="flex-1 flex flex-col gap-4 overflow-auto min-h-0 px-5 py-6 md:px-11 md:py-7"
          style={{ color: "var(--zen-text)" }}
        >
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-sm hover:opacity-70 transition-opacity self-start flex-shrink-0"
              style={{ color: "var(--zen-sub)" }}
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}

          {title && (
            <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: "clamp(18px, 2vw, 21px)", letterSpacing: uppercaseTitle ? "0.04em" : "0.02em", textTransform: uppercaseTitle ? "uppercase" : "none", color: "var(--zen-text)" }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div className="font-curator" style={{ fontWeight: 600, fontSize: "clamp(14px, 1.4vw, 17px)", color: "var(--zen-sub)", marginTop: -10 }}>
              {subtitle}
            </div>
          )}

          {children}

          {showContinue && onContinue && (
            <button
              onClick={onContinue}
              disabled={continueDisabled}
              data-testid="formshell-continue"
              className="flex-shrink-0"
              style={{
                marginTop: 4,
                padding: "15px 0",
                width: "100%",
                background: RED,
                border: "none",
                color: "#fff",
                fontFamily: '"Unbounded", sans-serif',
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: continueDisabled ? "default" : "pointer",
                opacity: continueDisabled ? 0.4 : 1,
                borderRadius: 2,
                transition: "opacity 0.2s",
              }}
            >
              {continueLabel} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
